import React, { useEffect, useMemo, useState } from "react";
import CardBase from "../../../components/cards/CardBase";
import PepperStepper from "../../components/common/PepperStepper";
import { Box, Button, Container, makeStyles, Dialog, DialogContent } from "@material-ui/core";
import { useHistory, useRouteMatch } from "react-router-dom";
import IncidentDetailsForm from "../../components/incidents/IncidentDetailsForm";
import IncidentPeopleForm from "../../components/incidents/IncidentPeopleForm";
import { useIncidentDetails } from "../../contexts/IncidentContext";
import IncidentForm from "../../components/incidents/IncidentForm";
import { useDispatch, useSelector } from "react-redux";
import useAddIncident from "../../hooks/mutations/useAddIncident";
import useIncidentResponses from "../../hooks/queries/useIncidentResponses";
import {
  clearForm,
  initialiseQuestionActions,
  setExistingResponses,
} from "../../../redux/actions/formCompleterActions";
import { useSnackbar } from "notistack";
import IncidentInvestigationForm from "../../components/incidents/IncidentInvestigationForm";
import useUpdateIncident from "../../hooks/mutations/useUpdateIncident";
import useIncidentForm from "../../hooks/queries/useIncidentForm";
import useCalculateFormActions from "../../hooks/mutations/useCalculateFormActions";
import CreatedActions from "../../components/formcompleter/CreatedActions";
import IncidentBuilder from "../../utils/IncidentBuilder";
import CustomDialog from "../../../components/CustomDialog";
import PageBase from "../../../components/PageBase";
import SiteSelect from "../../../components/SiteSelect";
import CustomButton from "../../../components/CustomButton";
import { incidentStatuses } from "../../constants/incidentStatus";
import InvestigationNotRequiredDialog from "../../components/dialogs/InvestigationNotRequiredDialog";
import CheckCircleIcon from "@material-ui/icons/CheckCircleOutline";
import { baseRoute } from "../../routes";
import { useParams } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
    stepperWrapper: {
        marginBottom: theme.spacing(2),
    },
    primaryButtons: {
        display: "flex",
        gap: theme.spacing(2),
    },
    secondaryButtons: {
        display: "flex",
    },
    buttonsContainer: {
        marginTop: theme.spacing(2),
        display: "flex",
        justifyContent: "end",
        flexWrap: "wrap",
    },
    actionButton: {
        [theme.breakpoints.down("sm")]: {
            flexGrow: 1,
        },
        [theme.breakpoints.down("xs")]: {
            width: "100%",
        },
    },
    confirmHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    incidentLogged: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        fontSize: "1.8em", fontWeight: "bold"
    },
    incidentNumber: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        fontSize: "1.3em"
    },
    dashboardBtn: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    checkIcon: {
        color: "green",
        fontSize: "5em",
    }
}));

const SAVE_WITHOUT_INVESTIGATION = "saveWithoutInvestigation";
const INITIAL_DETAILS_STEP = 0
const DESCRIPTION_MAX_LENGTH = 256

function AddEditIncident() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const { params } = match;
  const { incidentId } = params;
    const { displayConfirmation } = useParams();
    
  const {
    setActiveStep,
    activeStep,
    isFirstStep,
    isLastStep,
    goToPreviousStep,
    goToNextStep,
    currentCardTitle,
    currentCardDescription,
    incidentType,
    incidentDate,
    incidentDescription,
    people,
    templateVersionId,
    addResponsePeople,
    isIncidentFormStep,
    setIncidentType,
    setIncidentDate,
    setIncidentDescription,
    setTemplateVersionId,
    steps,
    stepsWithInvestigation,
    stepsWithActions,
    setSteps,
    initialSteps,
    isActionsStep,
    isInvestigationStep,
    resetIncident,
    incidentStatus,
    setIncidentStatus,
    investigationStatus,
    setInvestigationStatus,
    reasonInvestigationNotRequired,
  } = useIncidentDetails();
  const addIncident = useAddIncident();
  const updateIncident = useUpdateIncident();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedSite, selectedAccount } = useSelector((state) => state.account);
  const { questionResponses, uploads, actions } = useSelector(
    (state) => state.formCompleter
  );
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [newIncidentId, setNewIncidentId] = useState(null);

  const [
    investigationNotRequiredDialogOpen,
    setInvestigationNotRequiredDialogOpen,
  ] = useState(false);

  const incidentForm = useIncidentForm({
    siteExternalId: selectedSite?.externalId,
    incidentId,
    incidentType,
    accountId: selectedAccount.account.externalId,
    onSuccess: (data) => setTemplateVersionId(data.templateVersion.id),
  });

  const calculateActions = useCalculateFormActions();

  const hasInvestigation =
    incidentForm?.data?.templateVersion?.investigationSections?.length > 0;

  const incidentResponses = useIncidentResponses(incidentId);

  const investigationQuestionIds = useMemo(() => {
    return incidentForm?.data?.templateVersion?.investigationSections?.flatMap((s) =>
      s.questions.map((q) => q.id)
    );
  }, [incidentForm?.data?.templateVersion]);

  useEffect(() => {
    if (
      (!incidentResponses?.isFetching ||
        !incidentResponses?.isRefetching ||
        !incidentResponses?.isLoading) &&
      incidentResponses?.data?.incident?.people
    ) {
      addResponsePeople(incidentResponses?.data?.incident?.people);
      setIncidentDescription(incidentResponses?.data?.incident?.description);
      setIncidentDate(incidentResponses?.data?.incident?.incidentDate);
      setIncidentType(incidentResponses?.data?.incident?.type);
      setIncidentStatus(incidentResponses?.data?.incident?.status);
      setInvestigationStatus(
        incidentResponses?.data?.incident?.investigationStatus
      );
      dispatch(
        setExistingResponses(
          incidentResponses?.data?.templateFormQuestionResponses
        )
      );
    }
  }, [incidentResponses?.data]);

  useEffect(() => {
    if (
      hasInvestigation &&
      incidentForm?.data?.canCompleteIncidentInvestigation
    )
      setSteps(stepsWithInvestigation);
    if (!hasInvestigation || !incidentForm?.data?.canCompleteIncidentInvestigation) setSteps(initialSteps);
    if (activeStep > 2) setActiveStep(0);
  }, [hasInvestigation, selectedSite]);

  useEffect(() => {
    return () => {
      dispatch(clearForm());
      resetIncident();
    };
  }, []);

  const onBack = () => {
    if (isFirstStep) {
      dispatch(clearForm());
      resetIncident();
      history.goBack();
      return;
    }

    goToPreviousStep();
  };

  //TODO: Refactor this
  const onNext = async ({
    saveWithoutInvestigation = false,
    reasonInvestigationNotRequired,
  }) => {
    if (activeStep === INITIAL_DETAILS_STEP && incidentDescription?.length > DESCRIPTION_MAX_LENGTH) {
      enqueueSnackbar("Description must be no more than 256 characters.", {
        variant: "error",
      });
      return;
    }

    if (
      isLastStep ||
      saveWithoutInvestigation ||
      isInvestigationStep ||
      (isIncidentFormStep && investigationStatus === incidentStatuses.NA)
    ) {
      const completedForm = new IncidentBuilder(
        uploads,
        questionResponses,
        actions,
        incidentId,
        incidentDescription,
        incidentDate,
        incidentType,
        selectedSite?.externalId,
        people,
        hasInvestigation
      )
        .withTemplateVersionId(templateVersionId)
        .buildIncident()
        .buildResponses();

      if (reasonInvestigationNotRequired)
        completedForm.investigationNotRequired(reasonInvestigationNotRequired);

      if (investigationStatus !== incidentStatuses.NA)
        completedForm.investigationComplete(!saveWithoutInvestigation);

      const add = () =>
        addIncident.mutate(completedForm.build(), {
            onSuccess: (inc) => {
                if (displayConfirmation && !showConfirmationDialog) {
                    setNewIncidentId(inc.id);
                    setShowConfirmationDialog(true);
                    return;
                }
            dispatch(clearForm());
            resetIncident();
            history.goBack();
          },
          onError: (error) => {
            console.error(error);
            enqueueSnackbar("Could not save incident.", { variant: "error" });
          },
        });

      const update = () =>
        updateIncident.mutate(completedForm.build(), {
          onSuccess: () => {
            dispatch(clearForm());
            resetIncident();
            history.goBack();
          },
          onError: (error) => {
            console.error(error);
            enqueueSnackbar("Could not update incident.", { variant: "error" });
          },
        });

      const actionCalculationRequired =
        completedForm.result.templateFormResponses.filter(
          (x) =>
            investigationQuestionIds?.includes(x.templateQuestionId) &&
            (x.numberValue || x.optionId || x.optionIds?.length > 0)
        ).length > 0;

      const isInvestigationsWithActionCalculation =
        isInvestigationStep && actionCalculationRequired;

        if (isInvestigationsWithActionCalculation) {
        const calculatedActions = await calculateActions.mutateAsync(
            completedForm.forActionCalculation().build(),
          {
              onError: (error) => {
              console.error(error);
              enqueueSnackbar("Could not calculate actions.", {
                variant: "error",
              });
            },
          }
        );

        if (calculatedActions?.length > 0) {
          dispatch(initialiseQuestionActions(calculatedActions));
          setSteps(stepsWithActions);
          setActiveStep(4);
          return;
        }
      }

        completedForm.buildResponses(isActionsStep);

      if (!incidentId) {
        add();
        return;
      }

      update();
      return;
    }

    goToNextStep();
  };

  const onClickSaveWithoutInvestigation = () => {
    for (const questionId in questionResponses) {
      const q = questionResponses[questionId];
      if (
        investigationQuestionIds?.includes(q.templateQuestionId) &&
        (q.numberValue || q.optionId || q.optionIds?.length > 0)
      ) {
        setIsOpen(true);
        return;
      }
    }
    onNext({ saveWithoutInvestigation: true });
  };

    const handleSubmit = (e) => {
      e.preventDefault();
    const { name } = e.nativeEvent.submitter;
    if (name === SAVE_WITHOUT_INVESTIGATION) {
      onClickSaveWithoutInvestigation();
      return;
    }
    onNext({ saveWithoutInvestigation: false });
  };

  const canViewInvestigationNotRequiredButton =
    isIncidentFormStep &&
    hasInvestigation &&
    incidentForm?.data?.canCompleteIncidentInvestigation &&
    incidentStatus === incidentStatuses.COMPLETED &&
        investigationStatus !== incidentStatuses.NA;

    function handleOnDone() {
        dispatch(clearForm());
        resetIncident();
        history.push(`${baseRoute}`);
        setShowConfirmationDialog(false);
    }

  return (
    <PageBase>
      <Container>
        <InvestigationNotRequiredDialog
          open={investigationNotRequiredDialogOpen}
          onCancel={() => setInvestigationNotRequiredDialogOpen(false)}
          onSave={() =>
            onNext({
              saveWithoutInvestigation: true,
              reasonInvestigationNotRequired: reasonInvestigationNotRequired,
            })
          }
        />
        <div className={classes.stepperWrapper}>
          <CardBase>
            <PepperStepper steps={steps} activeStep={activeStep} />
          </CardBase>
        </div>
        <form onSubmit={handleSubmit}>
          <CardBase
            title={currentCardTitle}
            description={currentCardDescription}
          >
            {isFirstStep && <IncidentDetailsForm />}
            {activeStep === 1 && <IncidentPeopleForm />}
            {activeStep === 2 && (
              <>
                <Box my={2}>
                  <SiteSelect />
                </Box>
                <IncidentForm incidentForm={incidentForm} />
              </>
            )}
            {hasInvestigation && activeStep === 3 && (
              <IncidentInvestigationForm incidentForm={incidentForm} />
            )}
            {activeStep === 4 && (
              <CreatedActions siteExternalId={selectedSite?.externalId} />
            )}
          </CardBase>
          <div className={classes.buttonsContainer}>
            <div className={classes.primaryButtons}>
              <Button
                className={classes.actionButton}
                onClick={onBack}
                variant="outlined"
                color="primary"
              >
                Back
              </Button>
              {isIncidentFormStep &&
                hasInvestigation &&
                incidentForm?.data?.canCompleteIncidentInvestigation && (
                  <CustomButton
                    className={classes.actionButton}
                    variant="contained"
                    color="primary"
                    type="submit"
                    name={SAVE_WITHOUT_INVESTIGATION}
                    isLoading={
                      addIncident.isLoading || updateIncident.isLoading
                    }
                  >
                    Save and Exit
                  </CustomButton>
                )}
              {!(isIncidentFormStep && !selectedSite) && (
                <CustomButton
                  className={classes.actionButton}
                  variant="contained"
                  color="primary"
                  type="submit"
                  isLoading={addIncident.isLoading || updateIncident.isLoading}
                >
                  {isIncidentFormStep &&
                  hasInvestigation &&
                  investigationStatus !== incidentStatuses.NA &&
                  incidentForm?.data?.canCompleteIncidentInvestigation
                    ? "Continue to Investigation"
                    : isLastStep ||
                      (!hasInvestigation && isIncidentFormStep) ||
                      (hasInvestigation &&
                        investigationStatus === incidentStatuses.NA &&
                        isIncidentFormStep)
                    ? "Save"
                    : "Next"}
                </CustomButton>
              )}
              {canViewInvestigationNotRequiredButton && (
                <Button
                  className={classes.actionButton}
                  onClick={() => setInvestigationNotRequiredDialogOpen(true)}
                  variant="contained"
                  color="primary"
                >
                  Investigation Not Applicable
                </Button>
              )}
            </div>
          </div>
        </form>
        <CustomDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          confirmButtonText="Save"
          content="Changes you have made to the Investigation will not be saved. Are you sure you want to save?"
          title="Save without investigation?"
          onConfirm={() => onNext({ saveWithoutInvestigation: true })}
        />
          </Container>
          <Dialog
              maxWidth="sm" 
              fullWidth
              open={showConfirmationDialog}
          >
              <DialogContent dividers>
                  <div className={classes.confirmHeader}>
                      <CheckCircleIcon className={classes.checkIcon} />
                  </div>
                  <div className={classes.incidentLogged}>
                      Incident Logged
                  </div>
                  <div className={classes.incidentNumber}>
                      Incident number : {newIncidentId}
                  </div>
                  <div className={classes.dashboardBtn}>
                      <Button variant="contained" color="secondary" onClick={handleOnDone}>
                          Go to Dashboard
                      </Button>
                  </div>
              </DialogContent>
          </Dialog>
    </PageBase>
  );
}

export default AddEditIncident;
